/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging */
import { DateTime } from "luxon";

import User from "./User";
import Filters from "./filters";
import Balances from "./Balances";
import ApiHttp from "./ApiHttp";
import utils from "./utils";
import { ACCOUNT_PRODUCT_GROUP } from "./dbbl/businessLogic/entities/accounts/consts";
import { formatNumber } from "./dbbl/businessLogic/entities/utils/currency";
import { maskAccountNumber } from "./dbbl/utils/masks/accountDestination.mask";

export const AccountType = {
  Deposit: "deposit",
  Credit: "credit",
  Debit: "debit",
} as const;

export const ProductType = {
  Savings: "savings",
  Checking: "checking",
  CertificateOfDeposit: "certificate_of_deposit",
  HSA: "hsa",
  IRA: "ira",
  IRACertificateOfDeposit: "ira_certificate_of_deposit",
  MoneyMarket: "money_market",
  LineOfCredit: "line_of_credit",
  CreditCard: "credit_card",
  Loan: "loan",
  Mortgage: "mortgage",
  Lease: "lease",
  Unknown: "unknown",
} as const;

type LoanDetails = {
  minimum_payment: number;
  interest_rate: number;
  next_payment_at: string;
};

type AccountMetadata = {
  [key: string]: {
    value: null | string;
    public: boolean; // only public items are returned to clients, but the client may want to make them non-public
    format: null | "date" | "currency" | "percentage";
  };
};

type Organization = {
  uuid: string;
  name_legal: string;
  name_dba: string | null;
  ach_dual_approval_required: boolean;
  dual_approval_required: boolean;
  dual_approval_user_control: boolean;
  international_wire_dual_approval_required: boolean;
  address: API.Address | null;
  ach_dual_approval_expiration_grace_period_days: number | null;
  affiliated_organizations: AffiliatedOrganization[];
};

type AffiliatedOrganization = {
  uuid: string;
  name_legal: string;
  name_dba: string | null;
  core_id: string;
};

type ExternalAccountSyncProvider = "mx" | null;
type ExternalAccountSyncStatus = "connected" | "disconnected" | null;

interface BaseAccount {
  id: API.AccountId;
  name: string;
  nickname: string;
  features: string[];
  number: string;
  ach_pull_authorization_number: string;
  product: {
    id: string | null;
    type: API.ProductType;
    description?: string;
  };
  users: API.UserId[];
  hidden: boolean;
  favorited: boolean;
  loan_details: LoanDetails | EmptyObject;
  verified: boolean;
  updated_from_source_at: Timestamp | null;
  check_micr: null | string;
  fi_name: string;
  fi_svg: string;
  created_at: USDateString;
  updated_at: Timestamp;
  organization: Organization | null;
}

declare global {
  namespace API {
    type AccountId = Brand<string, "AccountId">;
    type MembershipId = Brand<string, "MembershipId">;

    type AccountType = (typeof AccountType)[keyof typeof AccountType];
    type ProductType = (typeof ProductType)[keyof typeof ProductType];

    interface Account extends BaseAccount {
      type: AccountType;
      source: "institution";
      metadata: AccountMetadata;
      balances: AccountBalances;
      state: "active";
      routing: "";
    }

    interface Loan extends Account {
      loan_details: LoanDetails;
    }
    interface ExternalAccount extends BaseAccount {
      type: AccountType;
      source: "external";
      balances: AccountBalances;
      metadata: EmptyObject;
      state: "pending" | "verified" | "removed";
      routing: `${number}`;
      synced_with: ExternalAccountSyncProvider;
      sync_status: ExternalAccountSyncStatus;
    }

    type AnyAccount = Account | ExternalAccount;
  }
}

type MetadataFormatters = Record<
  Exclude<AccountMetadata[string]["format"], null>,
  (...params: any[]) => string
>;
export type MetadataDetailFluentValue = {
  readonly id: string;
  args: { value: string | number };
  default: string | undefined;
};
type MetadataDetail = {
  label: string;
  value: string | MetadataDetailFluentValue | null;
  fluentId?: string;
};
type FormattedAccountData = Record<string, MetadataDetail>;
type AccountDetailsResult = {
  defaultDetails: MetadataDetail[];
  expandedDetails: MetadataDetail[];
};

const defaultFormatters: MetadataFormatters = {
  currency: Filters.currency,
  percentage: Filters.percent,
  date: Filters.americanDate,
} as const;

function isNotEmpty<T extends object>(data: T | EmptyObject): data is T {
  return !!data && Object.keys(data).length > 0;
}

type DeserializedAccount = Omit<
  API.AnyAccount,
  "balances" | "users" | "loan_details"
> & {
  balances: Balances;
  account_type: API.Account["type"];
  product_type: API.Account["product"]["type"];
  users: User[];
  synced_with: API.ExternalAccount["synced_with"];
  sync_status: API.ExternalAccount["sync_status"];
  loan_details:
    | EmptyObject
    | (Omit<LoanDetails, "minimum_payment"> & { minimum_payment: string });
};

type AccountProps = Partial<DeserializedAccount>;

interface AccountHeadlineType {
  title: string;
  type: string;
  value: string;
  nextPaymentDate?: string;
}

type StopCheckPaymentType =
  | { min_check_number: string; amount: string; max_check_number?: never }
  | { min_check_number: string; amount?: never; max_check_number: string };

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface Account extends DeserializedAccount {}

class Account {
  constructor(props: AccountProps) {
    Object.assign(this, props);
    // External accounts shouldn't ever be favorited,
    // for now just hardcode this until we feel right to do a data migration
    this.favorited = props.source === "institution" ? !!props.favorited : false;
    this.hidden = !!props.hidden;
    this.users = props.users || [];
    this.features = props.features || [];
    this.loan_details = { ...(props.loan_details || {}) };
    this.metadata = { ...(props.metadata || {}) };
    this.balances = props.balances || new Balances({});
  }

  // Metadata keys that have fluent based value formatting.
  // Fluent ids are written in the form `account-detail-${kebab-case-key}-custom-value`
  readonly FLUENT_BASED_ACCOUNT_DETAIL_VALUE_KEYS = new Set(["interest_rate"]);

  // taken from integrations.base Account types, also includes the EXTERNAL_ACCOUNTS group
  static PRODUCT_GROUPS = {
    Favorites: ["favorites"],
    Checking: ["checking"],
    Savings: ["savings", "hsa", "ira", "money_market"],
    "Credit cards": ["credit_card"],
    Loans: ["line_of_credit", "mortgage", "lease", "loan"],
    CDs: ["certificate_of_deposit", "ira_certificate_of_deposit"],
    Others: ["unknown"],
    "External accounts": ["external_account"],
  };

  /**
   * Translates a server group name (e.g., "credit_cards") to a display name (e.g., "Credit cards").
   * Uses localizedProductGroups if provided, otherwise uses the default PRODUCT_GROUPS.
   *
   * @param {string} serverGroupName - The server-format group name (e.g., "credit_cards", "checking")
   * @param {Record<string, string[]>} [localizedProductGroups] - Optional localized product groups mapping
   * @returns {string} The display name for the group (e.g., "Credit cards")
   */
  static getLocalizedGroupName(
    serverGroupName: string,
    localizedProductGroups?: Record<string, string[]>,
  ): string {
    const groupsToSearch = localizedProductGroups || Account.PRODUCT_GROUPS;
    const groupName = Object.keys(groupsToSearch).find((key) => {
      const groupKey = key.toLowerCase().replace(/ /g, "_");
      return groupKey === serverGroupName;
    });
    return groupName || serverGroupName;
  }

  getProductGroup(localizedProductGroups?: Record<string, string[]>) {
    // Get the product group type, ignoring the "Favorites" special case
    const wasFavorited = this.favorited;
    this.favorited = false;
    const type = this.getGroupName(localizedProductGroups);
    this.favorited = wasFavorited;
    return type;
  }

  getBalanceHeadline(): AccountHeadlineType[] {
    const group = this.getProductGroup();
    const isCurrentBalanceHeadline =
      this.account_type === "deposit" ||
      ["credit_card", "mortgage"].includes(this.product_type || "");

    switch (group) {
      case ACCOUNT_PRODUCT_GROUP.CHECKING:
      case ACCOUNT_PRODUCT_GROUP.SAVINGS:
        return [
          {
            type: "available-balance",
            title: "Available balance",
            value: this.balances.available,
          },
        ];

      default:
        if (isCurrentBalanceHeadline) {
          return [
            {
              type: "current-balance",
              title: "Current balance",
              value: this.balances.ledger,
            },
          ];
        }
        return [
          {
            type: "default",
            title: "Balance",
            value: this.balances.ledger,
          },
        ];
    }
  }

  getPaymentHeadlines(): AccountHeadlineType[] {
    if (["", null, undefined].includes(this.loan_details?.minimum_payment))
      return [];

    let formattedDate = "";

    const paymentDetails: string[] = [
      this.product_type === "credit_card" ? "Minimum payment" : "Next payment",
    ];
    const type =
      this.product_type === "credit_card" ? "minimum-payment" : "next-payment";

    // display when the next payment is due if it exists
    if (this.loan_details?.next_payment_at) {
      formattedDate = Filters.americanDate(this.loan_details.next_payment_at);
      const paymentDueDate = `(due ${formattedDate})`;
      paymentDetails.push(paymentDueDate);
    }
    return [
      {
        type,
        nextPaymentDate: formattedDate,
        title: paymentDetails.join(" "),
        value: formatNumber(this.loan_details.minimum_payment),
      },
    ];
  }

  getMiscHeadlines(): AccountHeadlineType[] {
    const miscHeadlines: AccountHeadlineType[] = [];

    // Display the maturity date if it exists and is public.
    // formattedData() may still drop the field if formatting produces an
    // empty value, so guard the formatted lookup as well.
    if (
      this.metadata?.maturity_date?.value &&
      this.metadata?.maturity_date?.public
    ) {
      const maturityDate = this.formattedData().maturity_date;
      if (maturityDate) {
        miscHeadlines.push({
          type: "maturity-date",
          title: "Maturity date",
          value: maturityDate.value as string,
        });
      }
    }

    return miscHeadlines;
  }

  displayHeadlineData() {
    const contents: AccountHeadlineType[] = [];
    contents.push(...this.getBalanceHeadline());
    contents.push(...this.getPaymentHeadlines());
    contents.push(...this.getMiscHeadlines());
    return contents;
  }

  getMaskedNumber(accountNumber = this.number): string {
    return maskAccountNumber(accountNumber || "");
  }

  getName() {
    return this.nickname || this.name || "";
  }

  getNameWithoutMaskedAccountNumber() {
    // (\*{2,}[0-9]{2,})?  Two or more stars and two or more numbers is present or not present.
    // ([-=])?             Match one or none for dash or equals.
    // *{2,}               Two or more stars.
    // [0-9]{2,}$          Two or more numbers at the end of the string.
    const maskedAccountNumberRegex =
      /(\*{2,}[0-9]{2,})?([-=])?\*{2,}[0-9]{2,}$/;
    return this.name?.replace(maskedAccountNumberRegex, "").trim() || "";
  }

  // to be used with <TruncatedAccount /> component
  getTruncatedValues(isDestination = false) {
    const name = this.getName();
    const isExternal = this.isExternal();
    // External account names are serialized to include the number, so don't add the number to the customer facing name
    const maskedNumber = isExternal ? null : this.getMaskedNumber();
    const balance = this.getRelevantBalance(isDestination);

    let endingValue = "";

    if (isExternal) {
      endingValue = balance != null ? `${balance}` : "";
    } else if (maskedNumber && balance != null) {
      endingValue = `${maskedNumber}: ${balance}`;
    } else if (maskedNumber) {
      endingValue = maskedNumber;
    } else if (balance != null) {
      endingValue = balance;
    }

    // name as "Test Checking Account" and endingValue as "0030: $1,523.50"
    return { name, endingValue };
  }

  // to be used with <TruncatedAccount /> component
  getTruncatedValuesWithoutBalance() {
    const name = this.getName();
    const isExternal = this.isExternal();
    // External account names are serialized to include the number, so don't add the number to the customer facing name
    const maskedNumber = isExternal ? null : this.getMaskedNumber();
    const endingValue = maskedNumber ?? "";

    // name as "Test Checking Account" and endingValue as "0030"
    return { name, endingValue };
  }

  getShortDescription(includeSpaces = true): string {
    const nicknameOrName = this.getName();
    // External account names are serialized to include the number, so don't add the number to the customer facing name
    if (this.isExternal()) {
      return nicknameOrName;
    }
    const separator = includeSpaces ? " - " : "-";
    return `${nicknameOrName}${separator}${this.getMaskedNumber()}`;
  }

  getRelevantBalance(isDestination = false) {
    // use available balance unless it is a credit account used as a destination
    const relevantBalance =
      isDestination && this.isCredit()
        ? this.balances?.ledger
        : this.balances?.available;
    return relevantBalance;
  }

  getDescription(isDestination = false) {
    const shortDescription = this.getShortDescription();
    // use available balance unless it is a credit account used as a destination
    const relevantBalance = this.getRelevantBalance(isDestination);
    if (!relevantBalance) {
      return shortDescription;
    }
    return `${shortDescription}: ${relevantBalance}`;
  }

  getGroupName(localizedProductGroups?: Record<string, string[]>) {
    if (this.favorited) return "Favorites";
    const productType = this.isExternal()
      ? "external_account"
      : this.product_type;
    const groupsToUse = localizedProductGroups || Account.PRODUCT_GROUPS;
    return Object.entries(groupsToUse).reduce((prevGroupName, current) => {
      const [groupName, productTypes] = current;
      if (
        productTypes.includes(
          String(productType).toLowerCase().replace(/ /g, "_"),
        )
      )
        return groupName;
      return prevGroupName;
    }, "Others");
  }

  isInternal() {
    return this.source === "institution";
  }

  isExternal() {
    return !this.isInternal();
  }

  isCredit() {
    return this.account_type === "credit";
  }

  isDeposit() {
    return !this.isCredit();
  }

  isCD() {
    return Account.PRODUCT_GROUPS.CDs.includes(this.product_type || "");
  }

  isChecking() {
    return Account.PRODUCT_GROUPS.Checking.includes(this.product_type || "");
  }

  isInternalLoan() {
    return (
      this.isInternal() &&
      Account.PRODUCT_GROUPS.Loans.includes(this.product_type || "")
    );
  }

  isValidTransferSource() {
    return (
      this.features.indexOf("transfer_source") > -1 &&
      !this.features.includes("zero_balance")
    );
  }

  isValidM2mSource() {
    return (
      this.features.includes("m2m_source") &&
      !this.features.includes("zero_balance")
    );
  }

  isValidM2mDestination() {
    return this.features.includes("m2m_destination");
  }

  isValidAchTransferSource() {
    // balance check was performed before granting this feature to the account
    return this.features.includes("ach_source");
  }

  isValidTransferDestinationForSource(
    sourceAccount: Account | undefined | null,
    achAllowsPush: boolean,
    supportsInternalTransfers = true,
    areLoansAllowed: boolean = true,
  ) {
    // if loans are not allowed in the regular transfer flow, hide loan accounts from simple transfers options
    if (!areLoansAllowed && this.isInternalLoan()) return false;
    if (!sourceAccount) {
      return (
        this.isValidTransferDestination() ||
        (achAllowsPush && this.isValidAchTransferDestination())
      );
    }
    if (sourceAccount.id === this.id) return false;
    if (sourceAccount.isExternal()) {
      // Assume here that ACH pull must be allowed for external source to have been chosen
      return this.isInternal() && this.isValidAchTransferDestination();
    }
    // at this point, we know that the source account is internal
    // destination cannot be internal if internal transfers are not supported
    if (!supportsInternalTransfers && this.isInternal()) return false;
    // if the source account does not support ACHs, destination cannot be external
    if (!sourceAccount.isValidAchTransferSource() && this.isExternal())
      return false;

    return this.isValidTransferDestination();
  }

  isValidTransferSourceForDestination(
    destinationAccount?: Account | null,
    supportsInternalTransfers = true,
    supportsLoanPaymentsByExternalAch = true,
    areLoansAllowed = true,
  ) {
    if (!areLoansAllowed && this.isInternalLoan()) return false;
    if (!destinationAccount) {
      return this.isValidTransferSource();
    }
    if (destinationAccount.id === this.id) return false;
    if (destinationAccount.isExternal()) {
      // Assume here that ACH pull must be allowed for external destination to have been chosen
      return this.isInternal() && this.isValidAchTransferSource();
    }
    // at this point, we know that the destination account is internal
    // source cannot be internal if internal transfers are not supported
    if (!supportsInternalTransfers && this.isInternal()) return false;
    // if the destination account does not support ACHs, source cannot be external
    if (this.isExternal()) {
      if (!destinationAccount.isValidAchTransferDestination()) return false;
      // if loan payments by external ACH are not supported, source cannot be external account
      if (
        !supportsLoanPaymentsByExternalAch &&
        destinationAccount.isInternalLoan()
      ) {
        return false;
      }
    }

    return this.isValidTransferSource();
  }

  isValidTransferDestination() {
    return this.features.indexOf("transfer_destination") > -1;
  }

  isValidAchTransferDestination() {
    return this.features.indexOf("ach_destination") > -1;
  }

  isValidInternalOrExternalTransferSource(achAllowsPull: boolean) {
    return this.isValidTransferSource() && (achAllowsPull || this.isInternal());
  }

  isValidInternalOrExternalTransferDestination(achAllowsPush: boolean) {
    return (
      this.isValidTransferDestination() && (achAllowsPush || this.isInternal())
    );
  }

  isValidBillpaySource() {
    return this.features.includes("billpay_source");
  }

  isPayableByCard() {
    return (
      this.isInternal() &&
      ["mortgage", "lease", "loan"].includes(this.product_type || "")
    );
  }

  isAmortizedLoan() {
    return this?.metadata?.loan_amortized?.value === "yes";
  }

  availableBalanceAsFloat() {
    return this.balances.available
      ? parseFloat(this.balances.available.replace(/[^\d.-]/g, ""))
      : 0;
  }

  transferableBalanceAsFloat() {
    if (this.metadata.transferable_balance?.value) {
      return parseFloat(this.metadata.transferable_balance.value);
    }
    return this.availableBalanceAsFloat();
  }

  ledgerBalanceAsFloat() {
    return this.balances.ledger
      ? parseFloat(this.balances.ledger.replace(/[^\d.-]/g, ""))
      : 0;
  }

  // Checking/savings show available balance; everything else shows ledger.
  showsAvailableBalance() {
    return [
      ...Account.PRODUCT_GROUPS.Checking,
      ...Account.PRODUCT_GROUPS.Savings,
    ].includes(this.product_type || "");
  }

  displayBalance() {
    return this.showsAvailableBalance()
      ? this.balances.available
      : this.balances.ledger;
  }

  displayBalanceAsFloat() {
    return this.showsAvailableBalance()
      ? this.availableBalanceAsFloat()
      : this.ledgerBalanceAsFloat();
  }

  primaryBalanceAsFloat() {
    return this.balances.primary
      ? parseFloat(this.balances.primary.replace(/[^\d.-]/g, ""))
      : 0;
  }

  formattedData(
    externalFormatters: Partial<MetadataFormatters> = defaultFormatters,
    accountDetailsInformationBlacklist: Record<string, string[]> = {},
  ): FormattedAccountData {
    const noop = <T>(x: T) => x;
    const formatters = (format: keyof MetadataFormatters) =>
      externalFormatters[format] || defaultFormatters[format] || noop;
    if (this.metadata.previous_payment_at) {
      this.metadata.previous_payment_date = this.metadata.previous_payment_at;
      delete this.metadata.previous_payment_at;
    }
    if (this.metadata.regular_loan_payment) {
      this.metadata.regular_payment = this.metadata.regular_loan_payment;
      delete this.metadata.regular_loan_payment;
    }
    if (this.metadata.loan_amortized && this.metadata.loan_amortized.public) {
      this.metadata.loan_amortized = {
        ...this.metadata.loan_amortized,
        public: false,
      };
    }

    const formattedMetadata = Object.entries(this.metadata)
      .filter(([_, values]) => values && values.value && values.public) // eslint-disable-line no-unused-vars
      .reduce(
        (previous, [key, { format, value }]) => {
          try {
            const fluentId = Filters.snakeToFluentId(key, "account-detail");

            if (
              format === "percentage" &&
              this.FLUENT_BASED_ACCOUNT_DETAIL_VALUE_KEYS.has(key)
            ) {
              previous[key] = {
                label: Filters.humanize(Filters.titlecase(key)),
                fluentId,
                value: {
                  id: `${fluentId}-custom-value`,
                  args: { value: Number(value) * 100 },
                  default: formatters("percentage")(value),
                },
              };
            } else {
              previous[key] = {
                label: Filters.humanize(Filters.titlecase(key)),
                fluentId,
                value: formatters(format as keyof MetadataFormatters)(
                  format === "date"
                    ? DateTime.fromISO(value as string).toJSDate()
                    : value,
                ),
              };
            }
          } catch {
            /* if formatting errors out, omit from returned formatted metadata */
          }
          return previous;
        },
        {} as Record<string, MetadataDetail>,
      );

    const otherDetails = {
      accountNumber: {
        label: "Account number",
        fluentId: "account-detail-account-number",
        value: this?.number,
      },
      routingNumber: {
        label: "Routing number",
        fluentId: "account-detail-routing-number",
        value: this?.routing,
      },
      checkMicr: {
        label: "Check MICR",
        fluentId: "account-detail-check-micr",
        value: this?.check_micr,
      },
      availableCredit: {
        label: "Available credit",
        fluentId: "account-detail-available-credit",
        value: this?.balances.available,
      },
      minimumPayment: {
        label: "Minimum payment",
        fluentId: "account-detail-minimum-payment",
        value: this?.loan_details?.minimum_payment
          ? formatters("currency")(this.loan_details.minimum_payment)
          : undefined,
      },
      apr: {
        label: "APR",
        fluentId: "account-detail-apr",
        value: this?.loan_details?.interest_rate
          ? formatters("percentage")(this.loan_details.interest_rate)
          : undefined,
      },
    };

    const { id: productId } = this.product || {};
    const informationBlacklist = productId
      ? accountDetailsInformationBlacklist[productId]
      : undefined;

    const filteredData: Record<string, MetadataDetail> = {};
    Object.entries({ ...formattedMetadata, ...otherDetails }).forEach(
      ([key, value]) => {
        /**
         * If blacklist exists for this account's product type,
         * and the given attribute is present in the blacklist,
         * hide it.
         */
        if (informationBlacklist) {
          const [snake_key, camelKey] = [
            Filters.camelToSnake(key),
            Filters.snakeToCamel(key),
          ];

          if (
            informationBlacklist.includes(snake_key) ||
            informationBlacklist.includes(camelKey)
          ) {
            return;
          }
        }

        /**
         * Only show availableCredit for credit_card or line_of_credit accounts
         */
        if (
          key === "availableCredit" &&
          this.product_type !== "credit_card" &&
          this.product_type !== "line_of_credit"
        ) {
          return;
        }

        if (value?.label && value?.value) {
          filteredData[key] = value as MetadataDetail;
        }
      },
    );

    return filteredData;
  }

  accountDetails(
    externalFormatters: Partial<MetadataFormatters> = defaultFormatters,
    accountDetailsInformationBlacklist: Record<string, string[]> = {},
  ): AccountDetailsResult {
    const {
      payoff_amount,
      accountNumber,
      routingNumber,
      checkMicr,
      availableCredit,
      maturity_date,
      interest_rate,
      dividend_rate,
      minimumPayment,
      past_due,
      regular_payment,
      previous_payment_date,
      apr,
      ...remainingData
    } = this.formattedData(
      externalFormatters,
      accountDetailsInformationBlacklist,
    );

    return {
      defaultDetails: [
        payoff_amount,
        accountNumber,
        routingNumber,
        checkMicr,
        availableCredit,
        maturity_date,
        // some cores use interest rate, others use dividend rate
        interest_rate || dividend_rate,
        minimumPayment,
        past_due,
        regular_payment,
        previous_payment_date,
      ].filter((detail): detail is MetadataDetail => !!detail),
      // prettier-ignore
      expandedDetails: [
        apr,
        ...Object.values(remainingData)
      ].filter((detail): detail is MetadataDetail => !!detail),
    };
  }

  getPreviousPaymentDateValue() {
    /* `previous_payment_at` gets mutated into `previous_payment_date` so we should check both */
    return (this.metadata?.previous_payment_date?.value ||
      this.metadata?.previous_payment_at?.value) as DateString;
  }

  canCalculateLoanPayoff(userFeatures: API.UserFeatures) {
    return (
      this.product_type === "loan" &&
      !userFeatures.hide_loan_calculator &&
      Boolean(
        this.metadata &&
          this.metadata.interest_type &&
          this.metadata.interest_type.value === "365" &&
          this.metadata.interest_days &&
          this.metadata.interest_days.value &&
          this.getPreviousPaymentDateValue(),
      )
    );
  }

  calculatePayoffInfo(date: ConstructorParameters<typeof Date>[0]) {
    const from = DateTime.fromJSDate(
      new Date(this.getPreviousPaymentDateValue()),
    );
    const to = DateTime.fromJSDate(new Date(date));
    const days = to.diff(from, "days").as("days");
    const interestDays = Number(this.metadata.interest_days.value);
    const dayRate = this.loan_details.interest_rate / interestDays;
    const principal = utils.parseValueAsFloat(this.balances.ledger || 0) * 100;
    let compoundedBalance = principal;
    for (let i = 0; i < days; i += 1) {
      compoundedBalance = compoundedBalance * dayRate + compoundedBalance;
    }
    return {
      total: compoundedBalance,
      interest: compoundedBalance - principal,
      principal,
      payoffDate: date,
    };
  }

  stopCheckPayment(payload: StopCheckPaymentType) {
    if (payload?.amount && typeof payload.amount === "string") {
      (payload as any).amount = utils.dollarsToPennies(
        payload.amount.replace(/[^\d.]/g, ""),
      );
    }
    const url = `accounts/${this.id}/stops`;
    return ApiHttp.fetch(url, { method: "POST" }, payload);
  }

  withdrawCashiersCheck<T extends { amount: string }>(payload: T) {
    (payload as any).amount = utils.dollarsToPennies(
      payload.amount.replace(/[^\d.]/g, ""),
    );
    const url = `accounts/${this.id}/withdrawals`;
    return ApiHttp.fetch(url, { method: "POST" }, payload);
  }

  toggleFavorited() {
    return ApiHttp.fetch(
      `accounts/${this.id}`,
      { method: "PUT" },
      { favorited: !this.favorited },
    );
  }

  toggleHidden() {
    return ApiHttp.fetch(
      `accounts/${this.id}`,
      { method: "PUT" },
      { hidden: !this.hidden },
    );
  }

  /**
   * Notably, if the `accounts` param contains both deposit and credit accounts,
   * this will return the sum of the primary balance of deposit accounts ONLY.
   * This is because favorited account collections can include both types of accounts,
   * and mixing credit and deposit values is confusing.
   *
   * @param {Array} accounts - Account objects to sum the balance of
   * @returns {Number} the summed balance of all accounts.
   */
  static sumBalancesForAccounts(accounts: Account[]) {
    if (!accounts.every((a) => a.isInternal()))
      throw Error("must be internal accounts");
    const allCredit = accounts.every((a) => a.isCredit());
    const subset = !allCredit
      ? accounts.filter((a) => a.isDeposit())
      : accounts;
    return subset.reduce((pv, cv) => pv + cv.primaryBalanceAsFloat(), 0);
  }

  static getAccountFromId(accountId: Account["id"], accounts: Account[]) {
    if (!accounts || accounts.length < 1) {
      return null;
    }
    return accounts.find((account) => account.id === accountId);
  }

  static getTransactionType(
    to_account_id: Account["id"],
    from_account_id: Account["id"],
    accounts: Account[],
  ) {
    const toAccount = accounts.find((a) => a.id === to_account_id);
    const fromAccount = accounts.find((a) => a.id === from_account_id);
    const transactionType =
      toAccount?.account_type === "credit" ||
      fromAccount?.product_type === "certificate_of_deposit"
        ? "Payment"
        : "Transfer";
    return transactionType;
  }

  static addExternalAccount<T>(payload: T) {
    return ApiHttp.fetch(
      "accounts",
      { method: "POST" },
      {
        ...payload,
        ...{ agreement: true },
      },
    );
  }

  updateBalances(balances: Balances | API.AccountBalances) {
    if (balances.account_id && balances.account_id !== this.id) {
      /* empty */
    } else if (balances instanceof Balances) {
      this.balances = balances;
    } else {
      this.balances = Balances.deserialize({
        ...balances,
        account_id: this.id,
      });
    }
  }

  /**
   * Factory method returning a new instance of Account from
   * an indigo.serialized Account
   * */
  static deserialize(payload: API.AnyAccount) {
    const { balances, loan_details, ...rest } = payload;
    const loanDetails = isNotEmpty(loan_details)
      ? ({
          ...loan_details,
          minimum_payment: Filters.penniesToDollars(
            loan_details.minimum_payment,
          ).toString(),
        } as DeserializedAccount["loan_details"])
      : {};

    const account = new Account({
      ...rest,
      loan_details: loanDetails,
      account_type: payload.type,
      product_type: payload.product?.type,
      // WARNING: AccountUsers only contain the user UUID and nothing else
      users: payload.users?.map((payloadUser) => new User(payloadUser as any)),
      organization: payload.organization || null,
    });

    if (isNotEmpty(balances)) {
      account.updateBalances(balances);
    }

    return account;
  }

  serialize() {
    return {
      id: this.id,
      name: this.nickname,
      favorited: this.favorited,
    };
  }
}

export default Account;
export type { Account as AccountClassType };
